/**
 * Aggregated native tool (SMSWiser) performance across every drop in a campaign
 * group — the "All drops" view. Combines each drop's blocks per dimension and
 * defaults to grouping by drop (collapsible per-drop subtotals).
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC, useEffect, useMemo, useState } from 'react';
import { BarChart3, RefreshCw } from 'lucide-react';

import { Button, LoadingPanel } from '@/components/ui';
import type { GenericStatsRow } from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab';
import { useToolDropStatsMulti } from '@/hooks/smswiser/tool-stats';

import { TOOL_DIMENSIONS, toGenericStatsRowWithDrop } from './shared/tool-columns';
import { aggregateToolMetrics, toolMultiGroupOptions } from './shared/tool-grouping';
import { StatsSourceToggle } from './StatsSourceToggle';
import { ToolStatsTable } from './components/ToolStatsTable';

export interface ToolDropRef {
  uniqueId: string;
  label: string;
}

interface ToolStatsMultiViewProps {
  drops: ToolDropRef[];
}

export const ToolStatsMultiView: FC<ToolStatsMultiViewProps> = ({ drops }) => {
  const results = useToolDropStatsMulti(drops.map((d) => d.uniqueId));
  const [activeDim, setActiveDim] = useState<string>('');

  const loadedCount = results.filter((r) => r.data).length;
  const isFetching = results.some((r) => r.isFetching);

  const byDimension = useMemo(() => {
    const map = new Map<string, GenericStatsRow[]>();
    results.forEach((r, i) => {
      const drop = drops[i];
      if (!r.data || !drop) return;
      for (const block of r.data.blocks) {
        const rows = block.rows.map((row) =>
          toGenericStatsRowWithDrop(row, drop.uniqueId, drop.label),
        );
        map.set(block.dimension, [...(map.get(block.dimension) ?? []), ...rows]);
      }
    });
    return map;
  }, [results, drops]);

  const tabs = useMemo(
    () => TOOL_DIMENSIONS.filter((d) => (byDimension.get(d.dimension)?.length ?? 0) > 0),
    [byDimension],
  );

  useEffect(() => {
    if (tabs.length === 0) {
      if (activeDim) setActiveDim('');
      return;
    }
    if (!tabs.some((t) => t.dimension === activeDim)) setActiveDim(tabs[0]!.dimension);
  }, [tabs, activeDim]);

  if (loadedCount === 0 && isFetching) {
    return (
      <LoadingPanel
        size="lg"
        title="Loading native tool stats"
        description={`Aggregating ${drops.length} drops…`}
        className="min-h-[240px] border-primary/30 bg-primary/5"
      />
    );
  }

  if (tabs.length === 0) {
    return (
      <div className="px-4 py-8 text-sm text-muted-foreground">
        No native tool stats available for these drops yet.
      </div>
    );
  }

  const active = tabs.find((t) => t.dimension === activeDim) ?? tabs[0]!;
  const rows = byDimension.get(active.dimension) ?? [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <BarChart3 className="h-4 w-4 text-primary" />
          Tool (SMSWiser) performance — {loadedCount} of {drops.length} drops
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => results.forEach((r) => r.refetch())}
          disabled={isFetching}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <StatsSourceToggle
        value={active.dimension}
        onChange={setActiveDim}
        options={tabs.map((t) => ({ value: t.dimension, label: t.title }))}
        className="flex-wrap"
      />

      <ToolStatsTable
        key={active.dimension}
        rows={rows}
        totals={aggregateToolMetrics(rows)}
        groupOptions={toolMultiGroupOptions(active.dimension)}
        firstColumnLabel={active.firstColumnLabel}
        defaultGroupBy="drop"
      />
    </div>
  );
};

export default ToolStatsMultiView;
